import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { ReactElement, useEffect, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import ManagementForm from "../cybersec/components/cybersec-management/components/management-form/management-form";
import CalculatorContext, { Question, UserAnswer } from "./CalculatorContext";
import CalculatorResult from "./CalculatorResult";
import CalculatorYesNo from "./CalculatorYesNo";
import Form from "./Form";
import ProgressBar from "./ProgressBar";
import { questionsSchema } from "./validation/JsonValidation";
import { Language } from "./utils";
import BtnBgPlum70Dialog from "../../components/btn/btn-bg-plum-70-dialog";

/**
 * Used for pressing HTML string content of the ARTICLE into html (react was showing whole HTML code as string without interpretation)
 * @param content HTML string
 * @returns HTML
 */
function createMarkup(content: any) {
  return { __html: content };
}

export default function CalculatorProvider(): ReactElement {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsHeading, setQuestionsHeading] = useState<string>("");
  const [questionsDescription, setQuestionsDescription] = useState<string>("");
  const [viewDescription, setViewDescription] = useState<boolean>(true);
  const [selected, setSelected] = useState<Question>();
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [submitForm, setSubmitForm] = useState(false);
  const { translation, language } = Language();

  const setAnswer = (id: number, answer: string) => {
    setUserAnswers((prev) => {
      const exists = prev.find((a) => a.questionId === id);
      if (exists) {
        return prev.map((a) => (a.questionId === id ? { ...a, answer } : a));
      } else {
        return [...prev, { questionId: id, answer }];
      }
    });
  };

  useEffect(() => {
    async function loadData() {
      try {
        const json = await import(`./json/${id}-${language}.json`);
        const validated = await questionsSchema.validate(json.default);
        if (validated) {
          setQuestionsHeading(validated.heading);
          setQuestionsDescription(validated.description);
          setQuestions(validated.questions);
          setSelected(validated.questions[0]);
        }
      } catch (error) {
        navigate("/404");
      }
    }
    loadData();
  }, [id, navigate]);

  return (
    <CalculatorContext.Provider
      value={{
        questions,
        userAnswers,
        setSelected,
        setAnswer,
        setSubmitForm,
        translation: translation,
        language: language,
      }}
    >
      {questions.length > 0 && selected && (
        <div className="calculator-top">
          <Container>
            <div
              className={`d-flex ${!viewDescription ? "justify-content-center" : ""} `}
            >
              <h2 className="mb-5">{questionsHeading}</h2>
            </div>
            {questions.length != userAnswers.length &&
              !submitForm &&
              !viewDescription && (
                <ProgressBar
                  current={userAnswers.length}
                  total={questions.length}
                />
              )}

            <div
              className={
                questions.length === userAnswers.length ? "mt-5" : "mt-3"
              }
            >
              {questions.length > userAnswers.length && viewDescription ? (
                <div>
                  <div
                    className="paragraph-small ws-prewrap"
                    dangerouslySetInnerHTML={createMarkup(questionsDescription)}
                  />
                  <BtnBgPlum70Dialog
                    onClick={() => {
                      setViewDescription(false);
                    }}
                    buttonText={translation.start_calculator_button}
                  ></BtnBgPlum70Dialog>
                </div>
              ) : (
                questions.length > userAnswers.length && (
                  <Row className="align-items-center text-center">
                    <Col
                      lg="1"
                      md="6"
                      xs="6"
                      className="order-lg-1 order-2 d-flex flex-column align-items-end mt-4"
                    >
                      <Button
                        disabled={selected.id === questions[0].id}
                        variant="dark"
                        onClick={() => setSelected(questions[selected.id - 2])}
                        className="button-wo-border bg-divelit-plum-40 paragraph-small reverse-button d-flex align-items-center"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="" />
                      </Button>
                    </Col>
                    <Col lg="10" xs="12" className="order-1 order-lg-2">
                      <CalculatorYesNo data={selected} />
                    </Col>
                    <Col
                      lg="1"
                      md="6"
                      xs="6"
                      className="order-lg-3 order-3 d-flex flex-column align-items-start mt-4"
                    >
                      <Button
                        disabled={
                          userAnswers.find(
                            (a) => a.questionId === selected.id,
                          ) == undefined
                        }
                        variant="dark"
                        onClick={() =>
                          setSelected(
                            questions.find(
                              (cat) => cat.id === selected.id + 1,
                            ) || questions[0],
                          )
                        }
                        className="button-wo-border bg-divelit-plum-40 paragraph-small d-flex align-items-center"
                      >
                        <FontAwesomeIcon icon={faArrowRight} className="" />
                      </Button>
                    </Col>
                  </Row>
                )
              )}
              {questions.length === userAnswers.length &&
                submitForm === false && <Form />}
              {questions.length === userAnswers.length &&
                submitForm === true && (
                  <CalculatorResult
                    questions={questions}
                    userAnswers={userAnswers}
                    translation={translation}
                  />
                )}
            </div>
          </Container>
          <ManagementForm />
        </div>
      )}
    </CalculatorContext.Provider>
  );
}
